import * as React from "react";
import { Folder, Pencil, Trash2, Loader } from "lucide-react";
import { Button } from "../ui/button";
import { useDeleteRequestCategory } from "@/hooks/useDeleteRequestCategory";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Request, RequestCategory } from "@/types/research.types";
import { useRequestGallery } from "./RequestGalleryContext";
import RequestItem from "./RequestItem";
import { useDeleteRequest } from "@/hooks/useDeleteRequest";

const RequestList = React.lazy(() => import("./RequestList"));

interface RequestCategoryListProps {
  onRequestSelect: (content: string, request?: Request) => void;
}

const RequestCategoryList: React.FC<RequestCategoryListProps> = ({ onRequestSelect }) => {
  const {
    categories,
    isLoadingCategories,
    searchQuery,
    handleCategoryClick,
    currentParentId,
    setCurrentParentId,
    handleEditCategory,
    filteredCategories,
    filteredRequests,
    setEditingRequest,
    setMode,
  } = useRequestGallery();

  const deleteCategoryMutation = useDeleteRequestCategory();
  const deleteRequestMutation = useDeleteRequest();

  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] = React.useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = React.useState<string | null>(null);

  const [isDeleteRequestDialogOpen, setIsDeleteRequestDialogOpen] = React.useState(false);
  const [deletingRequestId, setDeletingRequestId] = React.useState<string | null>(null);

  const handleDeleteCategory = (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    setDeletingCategoryId(categoryId);
    setIsDeleteCategoryDialogOpen(true);
  };

  const confirmDeleteCategory = () => {
    if (deletingCategoryId) {
      deleteCategoryMutation.mutate(deletingCategoryId);
      setIsDeleteCategoryDialogOpen(false);
      setDeletingCategoryId(null);
    }
  };

  const handleDeleteRequest = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setDeletingRequestId(requestId);
    setIsDeleteRequestDialogOpen(true);
  };

  const confirmDeleteRequest = () => {
    if (deletingRequestId) {
      deleteRequestMutation.mutate(deletingRequestId);
      setIsDeleteRequestDialogOpen(false);
      setDeletingRequestId(null);
    }
  };

  const handleEditRequest = (e: React.MouseEvent, request: Request) => {
    e.stopPropagation();
    setEditingRequest(request);
    setMode("edit-request");
  };

  const onCategoryClick = (category: RequestCategory) => {
    if (!categories) return;
    const subCategories = categories.filter((c) => c.parent_id === category._id);
    if (subCategories.length > 0) {
      setCurrentParentId(category._id);
    } else {
      handleCategoryClick(category._id);
    }
  };

  const categoriesToShow = React.useMemo(() => {
    if (searchQuery) {
      return [...filteredCategories].reverse();
    }
    if (!categories) return [];
    let result;
    if (currentParentId) {
      result = categories.filter((c) => c.parent_id === currentParentId);
    } else {
      result = categories.filter((c) => !c.parent_id);
    }
    return [...result].reverse();
  }, [categories, currentParentId, searchQuery, filteredCategories]);

  return (
    <>
      {isLoadingCategories ? (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <Loader className="animate-spin" />
          <span className="ml-2">Loading categories...</span>
        </div>
      ) : (
        <>
          {categoriesToShow.length === 0 && filteredRequests.length === 0 && !currentParentId && (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center text-neutral-500 text-sm">
              <p>{searchQuery ? "No results found" : "No categories available"}</p>
            </div>
          )}

          {!searchQuery && !currentParentId && categoriesToShow.length > 0 && (
            <>
              <div
                className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
                onClick={() => handleCategoryClick("all")}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Folder className="size-3.5 text-neutral-400" />
                  <p className="text-sm font-medium text-neutral-400 truncate">ALL</p>
                </div>
              </div>

              <div
                className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
                onClick={() => handleCategoryClick("default")}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Folder className="size-3.5 text-neutral-400" />
                  <p className="text-sm font-medium text-neutral-400 truncate">DEFAULT</p>
                </div>
              </div>
            </>
          )}

          {categoriesToShow.map((category) => (
            <div
              key={category._id}
              className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
              onClick={() => onCategoryClick(category)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Folder className="size-3.5 text-neutral-400" />
                <p className="text-sm font-medium text-neutral-400 truncate">{category.name}</p>
              </div>
              <div className="flex items-center gap-3 ml-2">
                <Button
                  variant="ghost"
                  className="w-auto h-auto p-0"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditCategory(category);
                  }}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  className="w-auto h-auto p-0"
                  size="icon"
                  onClick={(e) => handleDeleteCategory(e, category._id)}
                >
                  <Trash2 className="size-3.5 text-danger-200" />
                </Button>
              </div>
            </div>

          ))}

          {searchQuery && filteredRequests.length > 0 && (
            <>
              {filteredRequests.map((request) => (
                <RequestItem
                  key={request.id}
                  request={request}
                  onClick={onRequestSelect}
                  onEdit={handleEditRequest}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </>
          )}

          {currentParentId && (
            <React.Suspense
              fallback={
                <div className="flex flex-col items-center justify-center p-4">
                  <Loader className="animate-spin" size={24} />
                </div>
              }
            >
              <RequestList onRequestSelect={onRequestSelect} />
            </React.Suspense>
          )}


          <Dialog open={isDeleteCategoryDialogOpen} onOpenChange={setIsDeleteCategoryDialogOpen}>
            <DialogContent className="p-4">
              <DialogHeader className="sm:text-center">
                <DialogTitle className="text-base font-medium text-center">Delete Category</DialogTitle>
                <DialogDescription className="text-center text-sm">
                  Are you sure you want to delete this category? All requests within this category will
                  also be deleted.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-row gap-2 sm:justify-center">
                <Button
                  variant="secondary"
                  onClick={() => setIsDeleteCategoryDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDeleteCategory} className="flex-1">
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isDeleteRequestDialogOpen} onOpenChange={setIsDeleteRequestDialogOpen}>
            <DialogContent className="p-4">
              <DialogHeader className="sm:text-center">
                <DialogTitle className="text-base font-medium text-center">Delete Request</DialogTitle>
                <DialogDescription className="text-center text-sm">
                  Are you sure you want to delete this request? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-row gap-2 sm:justify-center">
                <Button
                  variant="secondary"
                  onClick={() => setIsDeleteRequestDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDeleteRequest} className="flex-1">
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};

export default RequestCategoryList;
